:host {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    user-select: none;

    flex-shrink: 0;
    min-width: var(--m3-space-12);
    min-height: var(--m3-space-12);

    --btn-theme-color: var(--color-text1, #101010);
    --btn-on-theme-color: var(--color-primary1, #FFFFFF);

    color: var(--btn-theme-color);
    --btn-bg: transparent;
    --btn-border: none;
    --btn-width: var(--btn-height, 40px);
}

button {
    background-color: var(--btn-bg);
    border: var(--btn-border);
    color: inherit;

    padding: 0;
    cursor: pointer;
    outline: none;

    display: flex;
    align-items: center;
    justify-content: center;

    width: var(--btn-width, 40px);
    height: var(--btn-height, 40px);
    border-radius: var(--btn-border-rd);
    position: relative;

    transition: background-color 0.2s ease-in-out,
        color 0.2s ease-in-out,
        border 0.2s ease-in-out,
        border-radius 0.2s ease-in-out;
}

button::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: max(100%, var(--m3-space-12, 48px));
    height: max(100%, var(--m3-space-12, 48px));
}

button::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: currentColor;
    opacity: 0;
    transition: background-color 0.2s ease-in-out,
        color 0.2s ease-in-out,
        border 0.2s ease-in-out,
        border-radius 0.2s ease-in-out,
        opacity 0.2s ease-in-out;
    pointer-events: none;
}

@media (hover: hover) {
    button:hover::after {
        opacity: 0.08;
    }
}

button:focus-visible {
    outline: 3px solid var(--color-blue-action, #000000);
}

button:focus-visible::after {
    opacity: 0.10;
}

button:active::after {
    opacity: 0.10;
}

::slotted([slot=icon]) {
    width: var(--icon-size, 24px);
    height: var(--icon-size, 24px);
    fill: currentColor;
}

:host([k_disabled]) {
    pointer-events: none;
    --disabled-color: color-mix(in srgb, var(--color-text1, #000000) 38%, transparent);
    color: var(--disabled-color) !important;
}

:host([k_disabled][k_colorStyle=filled]),
:host([k_disabled][k_colorStyle=tonal]) {
    --btn-bg: color-mix(in srgb, var(--color-text1, #000000) 10%, transparent) !important;
    --btn-border: none !important;
}

:host([k_disabled][k_colorStyle=outlined]) {
    --btn-bg: none !important;
    --btn-border: 1px solid var(--color_borderbox1) !important;
}

button:disabled {
    cursor: not-allowed;
}

// COLOR CONFIG VARIANTS
:host([k_colorStyle=filled]) {
    --btn-bg: var(--btn-theme-color);
    color: var(--btn-on-theme-color);
}

:host([k_colorStyle=tonal]) {
    --btn-bg: var(--btn-tonal-bg, color-mix(in srgb, var(--btn-theme-color) 15%, transparent));
    color: var(--btn-theme-color);
}

:host([k_colorStyle=outlined]) {
    --btn-border: 1px solid var(--btn-theme-color);
}

// SIZE CONFIG VARIANTS
:host([k_size=extra-small]) {
    --w-default: 32px;
    --w-narrow: 28px;
    --w-wide: 40px;

    --btn-height: 32px;
    --icon-size: 20px;

    --border-rd-sq: 12px;
    --border-rd-ps: 8px;
}

:host([k_size=small]) {
    --w-default: 40px;
    --w-narrow: 32px;
    --w-wide: 52px;

    --btn-height: 40px;
    --icon-size: 24px;

    --border-rd-sq: 12px;
    --border-rd-ps: 8px;
}

:host([k_size=medium]) {
    --w-default: 56px;
    --w-narrow: 48px;
    --w-wide: 72px;

    --btn-height: 56px;
    --icon-size: 24px;

    --border-rd-sq: 16px;
    --border-rd-ps: 12px;
}

:host([k_size=large]) {
    --w-default: 96px;
    --w-narrow: 64px;
    --w-wide: 128px;

    --btn-height: 96px;
    --icon-size: 32px;

    --border-rd-sq: 28px;
    --border-rd-ps: 16px;
}

:host([k_size=extra-large]) {
    --w-default: 136px;
    --w-narrow: 104px;
    --w-wide: 184px;

    --btn-height: 136px;
    --icon-size: 40px;

    --border-rd-sq: 28px;
    --border-rd-ps: 16px;
}

// SHAPE CONFIG VARIANTS
:host([k_shape=round]) {
    --btn-border-rd: calc(var(--btn-height) / 2);
}

:host([k_shape=square]) {
    --btn-border-rd: var(--border-rd-sq);
}

:host([k_morph]):has(button:active) {
    --btn-border-rd: var(--border-rd-ps);
}

// WIDTH CONFIG VARIANTS
:host([k_width=narrow]) {
    --btn-width: var(--w-narrow);
}

:host([k_width=wide]) {
    --btn-width: var(--w-wide);
}